import { useState, useEffect } from 'react';
import { DataStore } from 'aws-amplify';
import { SSCC } from '../models';
import { SSCCProps, convertSsccToProps } from '../datastore/ssccs';
import { SsccStatus } from '../common';
import dayjs from 'dayjs';
import { cleanupDuplicateSSCCs, checkForDuplicateSSCCs } from '../common/helperFunctions/datastoreCleanup';
import { captureError } from '../common/sentry';
import { deduplicateSSCCs, resolveDateBounds, shouldIncludeSscc, upsertDeduplicated } from './useSubscribeSsccs.utils';

export default function useObserveSsccs(dateLimit: number | null) {
  const [ssccs, setSsccs] = useState<SSCCProps[]>([]);

  useEffect(() => {
    const retrieveData = async () => {
      try {
        const { today, endDateString } = resolveDateBounds(dateLimit);

        const entities: SSCC[] = await DataStore.query(SSCC, (c) =>
          c.and((c) => [c.open.between(endDateString, today)])
        );
        console.debug('useObserveSsccs:', entities.length, 'ssccs fetched from DataStore');

        const validSsccs = entities
          .map((sscc) => convertSsccToProps(sscc))
          .filter((sscc): sscc is SSCCProps => sscc !== undefined)
          .filter((sscc) => sscc.status !== SsccStatus.cancelled);

        // Apply deduplication to handle DataStore duplicates
        const deduplicatedSsccs = deduplicateSSCCs(validSsccs);

        if (deduplicatedSsccs.length !== validSsccs.length) {
          console.warn(`Removed ${validSsccs.length - deduplicatedSsccs.length} duplicate SSCCs`);
        }

        setSsccs(deduplicatedSsccs);
      } catch (error) {
        console.error('Error retrieving SSCCs:', error);
        captureError(error, 'useSubscribeSsccs-retrieveData');
      }
    };

    const subscription = DataStore.observe(SSCC).subscribe({
      next: (message: any) => {
        const opType = message?.opType as 'INSERT' | 'UPDATE' | 'DELETE' | undefined;
        const element = message?.element as SSCC | undefined;

        if (!opType || !element?.id) {
          return;
        }

        if (opType === 'DELETE') {
          setSsccs((previous) => previous.filter((item) => item.id !== element.id));
          return;
        }

        const converted = convertSsccToProps(element);
        if (!converted) {
          return;
        }

        setSsccs((previous) => {
          if (!shouldIncludeSscc(converted, dateLimit)) {
            return previous.filter((item) => item.id !== converted.id);
          }

          return upsertDeduplicated(previous, converted);
        });
      },
      error: (error) => {
        console.error('Error observing SSCC changes:', error);
        captureError(error, 'useSubscribeSsccs-observe');
      }
    });

    retrieveData();

    return () => {
      subscription.unsubscribe();
    };
  }, [dateLimit]);

  // Handle network status changes
  useEffect(() => {
    let onlineRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleOnline = async () => {
      // Add a small delay to allow DataStore to sync
      if (onlineRefreshTimeoutId) {
        clearTimeout(onlineRefreshTimeoutId);
      }

      onlineRefreshTimeoutId = setTimeout(async () => {
        try {
          // Check for duplicates and clean them up if found
          const hasDuplicates = await checkForDuplicateSSCCs();
          if (hasDuplicates) {
            console.log('Duplicates detected after reconnection, running cleanup...');
            await cleanupDuplicateSSCCs();
          }

          const date = dateLimit && dateLimit !== 0 ? dayjs().subtract(dateLimit, 'days') : null;
          const today = dayjs().add(1, 'day').toISOString();
          const endDateString = date?.toISOString() ?? '';

          const entities: SSCC[] = await DataStore.query(SSCC, (c) =>
            c.and((c) => [c.open.between(endDateString, today)])
          );
          console.debug('Post-reconnection refresh:', entities.length, 'ssccs fetched');

          const validSsccs = entities
            .map((sscc) => convertSsccToProps(sscc))
            .filter((sscc): sscc is SSCCProps => sscc !== undefined)
            .filter((sscc) => sscc.status !== SsccStatus.cancelled);

          const deduplicatedSsccs = deduplicateSSCCs(validSsccs);
          setSsccs(deduplicatedSsccs);

          console.log('SSCC data refreshed after network reconnection');
        } catch (error) {
          console.error('Error refreshing SSCCs after reconnection:', error);
          captureError(error, 'useSubscribeSsccs-refreshAfterReconnection');
        }
      }, 3000); // 3 second delay to allow sync and cleanup
    };

    const handleOffline = () => {};

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (onlineRefreshTimeoutId) {
        clearTimeout(onlineRefreshTimeoutId);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dateLimit]);

  return ssccs;
}
